import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  BookOpen,
  PenLine,
  ChevronRight,
  Info,
  Lightbulb,
  AlertTriangle,
  Plus,
  LockIcon,
  BookTemplate,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import NewPageDialog from "./NewPageDialog";
import PageTemplateDialog from "./PageTemplateDialog";
import { useTranslation } from "react-i18next";

interface EmptyPageProps {
  onConfigureClick?: () => void;
  isEditor?: boolean;
  noPages?: boolean; // true if user has no pages at all, false if just the current page is empty
  onCreateClick?: () => void;
}

const EmptyPage: React.FC<EmptyPageProps> = ({
  onConfigureClick,
  isEditor = false,
  noPages = false,
  onCreateClick,
}) => {
  const { page_id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleConfigureClick = () => {
    if (onConfigureClick) {
      onConfigureClick();
    } else if (page_id) {
      navigate(`/${page_id}/edit`);
    }
  };

  const handleCreateClick = () => {
    if (onCreateClick) {
      onCreateClick();
    } else {
      // Default create page behavior - navigate to create page route if available
      navigate("/create");
    }
  };

  // Determine title and message based on scenario
  const title = noPages
    ? t("home.emptyPage.noPagesTitle")
    : t("home.emptyPage.emptyPageTitle");

  const message = noPages
    ? t("home.emptyPage.noPagesMessage")
    : t("home.emptyPage.emptyPageMessage");

  return (
    <div className="flex items-center justify-center w-full">
      <Card className="max-w-3xl w-full border-none shadow-none">
        <CardHeader className="text-center">
          <div className="mx-auto bg-muted/50 rounded-full w-16 h-16 flex items-center justify-center mb-4">
            <Lightbulb className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
          <p className="text-muted-foreground mt-2">{message}</p>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-6 pb-6">
          {isEditor ? (
            /* If user is an editor, show configuration or creation button */
            !noPages ? (
              <div className="flex flex-col items-center gap-2">
                <Button
                  onClick={handleConfigureClick}
                  size="lg"
                  className="px-8 gap-2"
                >
                  <PenLine className="h-5 w-5" />
                  {t("home.emptyPage.configureThisPage")}
                </Button>
                <span>{t("home.emptyPage.or")}</span>
                <PageTemplateDialog />
              </div>
            ) : (
              <NewPageDialog createText={t("home.emptyPage.createNewPage")} />
            )
          ) : (
            /* If user is not an editor, show permission message */
            <div className="text-center p-4 border rounded-lg bg-muted/30 max-w-xl">
              <div className="flex justify-center mb-2">
                <LockIcon className="h-5 w-5 text-amber-500" />
              </div>
              <h3 className="font-medium mb-1">
                {noPages
                  ? t("home.emptyPage.noPermissionCreateTitle")
                  : t("home.emptyPage.noPermissionModifyTitle")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {noPages
                  ? t("home.emptyPage.noPermissionCreateMessage")
                  : t("home.emptyPage.noPermissionModifyMessage")}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl">
            <Card className="border border-muted hover:border-primary/50 hover:shadow transition-all cursor-pointer">
              <a
                href="https://odashboard.app/documentation"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4"
              >
                <div className="flex items-start gap-3">
                  <BookOpen className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h3 className="font-medium">
                      {t("home.emptyPage.documentation")}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t("home.emptyPage.documentationDescription")}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                </div>
              </a>
            </Card>
            <Card className="border border-muted hover:border-primary/50 hover:shadow transition-all cursor-pointer">
              <a
                href="https://odashboard.app/helpdesk/o-dashboard-2"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4"
              >
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h3 className="font-medium">
                      {t("home.emptyPage.needHelp")}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t("home.emptyPage.needHelpDescription")}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                </div>
              </a>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmptyPage;
